import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Custom hook for API operations with loading and error states
 * @returns {Object} { loading, error, execute, clearError }
 */
export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (apiCall, options = {}) => {
    const { 
      showLoadingToast = false, 
      showErrorToast = true, 
      showSuccessToast = false,
      successMessage = 'Operation completed successfully',
      // Retries are opt-in: only safe for idempotent calls (GETs). Mutations must
      // not auto-retry — a retry after the server already accepted a create would
      // duplicate it. Callers that know the call is idempotent pass retries: N.
      retries = 1,
      retryDelay = 1000
    } = options;

    setLoading(true);
    setError(null);
    
    if (showLoadingToast) {
      toast.loading('Processing...');
    }
    
    let lastError;
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const result = await apiCall();
        
        if (showSuccessToast) {
          toast.success(successMessage);
        }
        
        setLoading(false);
        return result;
      } catch (err) {
        lastError = err;
        // If it's a network error and we have retries left, wait and try again
        if ((err.code === 'ERR_NETWORK' || err.message === 'Network Error') && attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue;
        }
        
        // If it's not a network error or we're out of retries, break
        break;
      }
    }
    
    // Parse error message, handling Pydantic validation errors
    let errorMessage = 'An error occurred';
    if (lastError?.response?.data?.detail) {
      const detail = lastError.response.data.detail;
      if (Array.isArray(detail)) {
        // Handle Pydantic validation errors with user-friendly mapping
        const firstError = detail[0];
        if (firstError && firstError.msg) {
          // Map specific validation errors to user-friendly messages
          const errorMappings = {
            'String should have at least 8 characters': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit',
            'String should have at least 1 character': 'This field cannot be empty',
            'value is not a valid email address: An email address must have an @-sign.': 'Please enter a valid email address',
            'value is not a valid email address': 'Please enter a valid email address',
            'Invalid phone number. Enter a valid phone number with country code (e.g. +1 for US).': 'Enter a valid phone number with country code (e.g. +1 for US)',
            'Value error, Invalid phone number. Enter a valid phone number with country code (e.g. +1 for US).': 'Enter a valid phone number with country code (e.g. +1 for US)',
            'Password must contain at least one uppercase letter': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit',
            'Password must contain at least one lowercase letter': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit',
            'Password must contain at least one digit': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit',
            "Input should be 'runner' or 'runclub'": 'Please select a valid user type',
            'Name cannot be empty': 'Name is required',
            'Invalid URL format': 'Please enter a valid URL'
          };
          
          // Check for exact matches first
          if (errorMappings[firstError.msg]) {
            errorMessage = errorMappings[firstError.msg];
          } else {
            // Check for partial matches
            let foundMatch = false;
            for (const [key, value] of Object.entries(errorMappings)) {
              if (firstError.msg.includes(key)) {
                errorMessage = value;
                foundMatch = true;
                break;
              }
            }
            if (!foundMatch) {
              errorMessage = firstError.msg;
            }
          }
        } else {
          errorMessage = 'Please check your input and try again';
        }
      } else {
        errorMessage = detail;
      }
    } else if (lastError?.message) {
      errorMessage = lastError.message;
    }
    
    setError(errorMessage);
    
    if (showErrorToast) {
      toast.error(errorMessage);
    }
    
    setLoading(false);
    throw lastError;
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { 
    loading, 
    error, 
    execute, 
    clearError 
  };
};

/**
 * Custom hook for form operations with validation
 * @param {Object} initialValues - Initial form values
 * @param {Object} validationRules - Validation rules for each field
 * @returns {Object} Form state and handlers
 */
export const useForm = (initialValues = {}, validationRules = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const handleBlur = useCallback((name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Validate field
    if (validationRules[name]) {
      const error = validationRules[name](values[name]);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  }, [values, validationRules]);

  const validate = useCallback(() => {
    const newErrors = {};
    Object.keys(validationRules).forEach(field => {
      const error = validationRules[field](values[field]);
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [values, validationRules]);

  const handleSubmit = useCallback(async (onSubmit) => {
    if (!validate()) {
      return false;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit(values);
      return true;
    } catch (error) {
      console.error('Form submission error:', error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [values, validate]);

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  const setFieldValue = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
  }, []);

  const setFieldError = useCallback((name, error) => {
    setErrors(prev => ({ ...prev, [name]: error }));
  }, []);

  return {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    validate,
    reset,
    setFieldValue,
    setFieldError,
    setValues,
    setErrors
  };
};

/**
 * Custom hook for data fetching with caching
 * @param {Function} fetchFunction - Function to fetch data
 * @param {Array} dependencies - Dependencies for the fetch function
 * @param {Object} options - Options for the hook
 * @returns {Object} { data, loading, error, refetch }
 */
export const useFetch = (fetchFunction, dependencies = [], options = {}) => {
  const { 
    initialData = null, 
    enabled = true,
    refetchOnMount = true 
  } = options;
  
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Memoize the fetch function to prevent unnecessary re-renders
  const memoizedFetchFunction = useCallback(fetchFunction, dependencies);

  const fetchData = useCallback(async () => {
    if (!enabled) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Small delay only on first load to ensure backend is ready
      if (data === null) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      const result = await memoizedFetchFunction();
      setData(result);
      return result;
    } catch (err) {
      // Handle 401 errors gracefully - don't throw for optional endpoints
      // The apiService interceptor will handle critical endpoints
      if (err.response?.status === 401) {
        // For 401 errors on optional endpoints (Strava, connections, etc.), 
        // silently handle them without logging to console
        // This allows components to handle the error gracefully
        setError(err);
        setData(null);
        // Don't log 401 errors for optional endpoints to avoid console noise
        return null;
      }
      
      // For other errors, set error and throw
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [memoizedFetchFunction, enabled]);

  const refetch = useCallback(() => {
    return fetchData();
  }, [fetchData]);

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    if (refetchOnMount && enabled) {
      fetchData();
    }
  }, [fetchData, refetchOnMount, enabled]);

  return {
    data,
    loading,
    error,
    refetch
  };
};

/**
 * Custom hook for pagination
 * @param {Function} fetchFunction - Function to fetch paginated data
 * @param {Object} initialParams - Initial pagination parameters
 * @returns {Object} Pagination state and handlers
 */
export const usePagination = (fetchFunction, initialParams = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...initialParams
  });
  const [hasMore, setHasMore] = useState(true);

  const fetchData = useCallback(async (newParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const currentParams = { ...params, ...newParams };
      const result = await fetchFunction(currentParams);
      
      if (currentParams.page === 1) {
        setData(result);
      } else {
        setData(prev => [...prev, ...result]);
      }
      
      setHasMore(result.length === currentParams.limit);
      setParams(currentParams);
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchFunction, params]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchData({ page: params.page + 1 });
    }
  }, [fetchData, loading, hasMore, params.page]);

  const reset = useCallback(() => {
    setData([]);
    setParams({ ...params, page: 1 });
    setHasMore(true);
    fetchData({ page: 1 });
  }, [fetchData, params]);

  return {
    data,
    loading,
    error,
    params,
    hasMore,
    fetchData,
    loadMore,
    reset
  };
};
